
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LayoutDashboard, 
  ArrowRight, 
  AlertTriangle, 
  Database, 
  ListTodo, 
  CheckCircle,
  FileText,
  Server,
  Layers,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';

const UniversalJournalReadiness = ({ project }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/universal-journal/analyze`, {
        project_id: project?.id || 'current_project',
        use_mock_data: true
      });
      setData(response.data);
    } catch (err) {
      setError('Failed to fetch Universal Journal analysis. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [project]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <button 
          onClick={fetchData}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const renderOverview = () => (
    <div className="space-y-6">
      {/* AI Narratives */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center mb-4">
            <FileText className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold">CFO Executive Summary</h3>
          </div>
          <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
            {data.ai_narratives.cfo_summary}
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <div className="flex items-center mb-4">
            <Server className="w-5 h-5 text-purple-600 mr-2" />
            <h3 className="text-lg font-semibold">Architect's Rationale</h3>
          </div>
          <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
            {data.ai_narratives.architect_rationale}
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Risk Score</div>
          <div className="text-2xl font-bold text-orange-600">{data.gap_analysis.risk_score}/100</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Identified Gaps</div>
          <div className="text-2xl font-bold text-red-600">{data.gap_analysis.gaps.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Est. ACDOCA Rows</div>
          <div className="text-2xl font-bold text-blue-600">{(data.volume_estimates.estimated_acdoca_rows / 1000000).toFixed(1)}M</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="text-sm text-gray-500">Backlog Items</div>
          <div className="text-2xl font-bold text-green-600">{data.backlog.length}</div>
        </div>
      </div>
    </div>
  );

  const renderArchitecture = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Current State */}
      <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Layers className="w-5 h-5 mr-2 text-gray-500" />
          Current State (ECC)
        </h3>
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded">
            <div className="font-medium text-sm text-gray-500">General Ledger</div>
            <div className="mt-1">{data.current_state.fi_gl.gl_type}</div>
            <div className="text-xs text-gray-400 mt-1">
              Ledgers: {data.current_state.fi_gl.ledgers.map(l => l.ledger).join(', ')}
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="font-medium text-sm text-gray-500">CO-PA</div>
            <div className="mt-1">{data.current_state.co_pa.type}</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="font-medium text-sm text-gray-500">Asset Accounting</div>
            <div className="mt-1">{data.current_state.aa.type}</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="font-medium text-sm text-gray-500">Material Ledger</div>
            <div className="mt-1">{data.current_state.ml.active ? 'Active' : 'Inactive'}</div>
          </div>
        </div>
      </div>

      {/* Target Design */}
      <div className="bg-white p-6 rounded-lg shadow border border-blue-100">
        <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-700">
          <CheckCircle className="w-5 h-5 mr-2" />
          Target Design (S/4HANA)
        </h3>
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 rounded border border-blue-100">
            <div className="font-medium text-sm text-blue-600">Universal Journal (ACDOCA)</div>
            <div className="mt-1">
              {data.target_design.ledgers.map(l => (
                <div key={l.ledger} className="flex justify-between text-sm mt-1">
                  <span>{l.ledger} ({l.name})</span>
                  <span className="text-gray-500">{l.standard}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="p-3 bg-blue-50 rounded border border-blue-100">
            <div className="font-medium text-sm text-blue-600">CO-PA Strategy</div>
            <div className="mt-1 font-medium">{data.target_design.co_pa_strategy.recommendation}</div>
            <div className="text-xs text-gray-500 mt-1">{data.target_design.co_pa_strategy.rationale}</div>
          </div>
          <div className="p-3 bg-blue-50 rounded border border-blue-100">
            <div className="font-medium text-sm text-blue-600">Asset Accounting</div>
            <div className="mt-1">{data.target_design.asset_accounting.recommendation}</div>
          </div>
          <div className="p-3 bg-blue-50 rounded border border-blue-100">
            <div className="font-medium text-sm text-blue-600">Material Ledger</div>
            <div className="mt-1">{data.target_design.material_ledger.recommendation}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGaps = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Area</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Impact</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Severity</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.gap_analysis.gaps.map((gap, idx) => (
              <tr key={idx}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{gap.area}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{gap.issue}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{gap.impact}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                    ${gap.severity === 'Critical' ? 'bg-red-100 text-red-800' : 
                      gap.severity === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'}`}>
                    {gap.severity}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderMigration = () => (
    <div className="space-y-8">
      {/* Blueprint */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Conversion Blueprint</h3>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Table Mapping</h4>
              <ul className="space-y-2">
                {data.conversion_blueprint.mapping_rules.map((rule, idx) => (
                  <li key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-mono">{rule.source}</span>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-mono font-bold text-blue-600">{rule.target}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-700 mb-3">Conversion Steps</h4>
              <div className="space-y-4">
                {data.conversion_blueprint.conversion_steps.map((step) => (
                  <div key={step.step} className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold mt-0.5">
                      {step.step}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">{step.name}</div>
                      <div className="text-xs text-gray-500">{step.phase}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Backlog */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Migration Backlog</h3>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effort</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.backlog.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">{item.id}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.title}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{item.owner}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${item.effort === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>
                      {item.effort}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderImpact = () => {
    const chartData = [
      { name: 'Current (Tables)', size: 180 },
      { name: 'Target (ACDOCA)', size: data.volume_estimates.hana_memory_impact_gb },
      { name: 'After Archiving', size: data.volume_estimates.hana_memory_impact_gb - data.volume_estimates.archiving_potential.estimated_reduction_gb }
    ];

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">HANA Memory Impact (GB)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="size" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Archiving Recommendations</h3>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded border border-green-100">
              <div className="flex items-center mb-2">
                <Database className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium text-green-800">Potential Savings</span>
              </div>
              <div className="text-2xl font-bold text-green-700">
                {data.volume_estimates.archiving_potential.estimated_reduction_gb} GB
              </div>
              <div className="text-sm text-green-600 mt-1">
                By archiving closed fiscal years: {data.volume_estimates.archiving_potential.eligible_years.join(', ')}
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Archiving prior to migration significantly reduces downtime and HANA memory costs. 
              It is recommended to archive all closed years older than 2 years.
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Universal Journal Conversion Engine</h1>
          <p className="text-gray-500 mt-1">Phase 4: Target Architecture & Migration Design</p>
        </div>
        <button 
          onClick={fetchData}
          className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 shadow-sm"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Re-run Analysis
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: 'Overview', icon: LayoutDashboard },
            { id: 'architecture', label: 'Architecture', icon: Layers },
            { id: 'gaps', label: 'Gaps & Risks', icon: AlertTriangle },
            { id: 'migration', label: 'Migration Plan', icon: ListTodo },
            { id: 'impact', label: 'Volume & Impact', icon: Database },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              <tab.icon className={`
                -ml-0.5 mr-2 h-5 w-5
                ${activeTab === tab.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500'}
              `} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="min-h-[400px]">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'architecture' && renderArchitecture()}
        {activeTab === 'gaps' && renderGaps()}
        {activeTab === 'migration' && renderMigration()}
        {activeTab === 'impact' && renderImpact()}
      </div>
    </div>
  );
};

export default UniversalJournalReadiness;
